import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ViewportScroller } from '@angular/common';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
  ButtonPosition,
  ButtonSize,
  ButtonTheme,
  IconButtonSize,
  IconButtonTheme,
  IconPosition,
  LoaderTheme,
  ModalButtons,
  NotificationTheme,
  TagTheme,
} from '@fcom/ui-components';
import {
  Category,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairOperatingAirlineId,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairServiceItem,
  SeatInfo,
  SeatmapSvgAndControlData,
} from '@fcom/dapi/api/models';
import { isFinnairNorraOrWetLease, resolveTailName, stopPropagation, unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, SelectOption } from '@fcom/common/interfaces';
import { LanguageService } from '@fcom/ui-translate';
import { Amount, ServiceCatalogSelectionsForAllTravelers } from '@fcom/dapi/interfaces';
import { AIRCRAFT_TRANSLATION_MAP, SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import { ConfigService, SentryLogger } from '@fcom/core/services';
import { CmsTemplateService } from '@fcom/cms';
import { CmsTemplate } from '@fcom/core-api';
import { GtmService } from '@fcom/common/gtm';
import { finShare, snapshot } from '@fcom/rx';

import {
  EligibilitiesForFragments,
  FlightWithStreams,
  Notification,
  SeatChangeEvent,
  SeatDetails,
  SeatMapTraveler,
} from '../../../interfaces';
import { SeatMapRendererComponent } from '../seat-map-renderer/seat-map-renderer.component';
import {
  getSeatMapFlight,
  getSeatPrice,
  getServicesNotifications,
  isAllowedToChangeSeat,
  servicesForFlightAndTravelerId,
} from '../../../utils';
import { AncillaryLoaderStatus } from '../../../enums';
import { CommonBookingFeatureState, SeatMapActions } from '../../../../../store';
import { isFlight, isOrder } from '../../../../../utils/common-booking.utils';
import { SeatBenefitTag } from '../seat-benefit/seat-benefit.component';
import { BENEFITS } from '../../../../../utils/benefit.utils';

const FLIGHT_CONTAINER_ANIMATION_DELAY = 350;

interface OperatingAirlineIdInfo {
  operatingAirlineId: FinnairOperatingAirlineId;
  isOperated: boolean;
  isOrderIdIdentical: boolean;
  isOrder: boolean;
}

@Component({
  selector: 'fin-seat-map-view',
  styleUrls: ['./seat-map-view.component.scss'],
  templateUrl: './seat-map-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SeatMapViewComponent implements OnInit, OnDestroy, OnChanges {
  readonly ButtonPosition = ButtonPosition;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly Category = Category;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconPosition = IconPosition;
  readonly LoaderTheme = LoaderTheme;
  readonly ModalButtons = ModalButtons;
  readonly SeatMapStatus = SeatMapStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly NotificationTheme = NotificationTheme;

  @Input() flight: FlightWithStreams;
  @Input() passengers$: Observable<FinnairPassengerItem[]>;
  @Input() seatSelections$: Observable<ServiceCatalogSelectionsForAllTravelers>;
  @Input() flightIsActive$: Observable<boolean>;
  @Input() cartOrOrder$: Observable<FinnairCart | FinnairOrder>;
  @Input() includedSeats: FinnairServiceItem | null;
  @Input() availability$: Observable<EligibilitiesForFragments> = of({});
  @Input() canClearSeats = true;
  @Input() notEligibleWhenCheckInClosed = false;
  @Input() preSelectedPax$: Observable<string> = of(undefined);
  @Input() ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus>;
  @Input() skipScroll = false;

  @Output() changeSeats: EventEmitter<SeatChangeEvent> = new EventEmitter();
  @Output() subtotalChange: EventEmitter<Amount> = new EventEmitter();
  @Output() retrySeatmap: EventEmitter<FinnairItineraryItemFlight> = new EventEmitter();
  @Output() isReassigningSeats: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('seatMap') seatMapElement: SeatMapRendererComponent;

  subscriptions: Subscription = new Subscription();
  selectablePassengers$: BehaviorSubject<SeatMapTraveler[]> = new BehaviorSubject([]);
  seatDetails$: Subject<SeatDetails> = new ReplaySubject<SeatDetails>(1);
  passengerOptions$: Observable<SelectOption[]>;
  seatMapContainerWidth$: BehaviorSubject<number | undefined> = new BehaviorSubject(undefined);
  unmodifiableSeat$: Observable<boolean>;
  unmodifiableFreeSeat$: Observable<boolean>;
  noSeatmapNotifications$: Observable<Notification[]>;
  isAtStart = true;
  isAtEnd = false;
  currentFlightTail: string;
  exitRowModalOpen = false;
  aircraftModalOpen = false;
  aircraftInformationTemplate$: BehaviorSubject<CmsTemplate | undefined> = new BehaviorSubject(undefined);
  scrollPosition = 0;
  isFinnairWetLease = false;
  operatingAirlineIdInfo$: Observable<OperatingAirlineIdInfo>;
  flight$: BehaviorSubject<FlightWithStreams> = new BehaviorSubject<FlightWithStreams>(null);
  benefitTextKey: string;
  benefitTag: SeatBenefitTag;
  bundleActive$: Observable<boolean>;
  bundlesEnabled = false;

  constructor(
    private languageService: LanguageService,
    private templateService: CmsTemplateService,
    private configService: ConfigService,
    private viewportScroller: ViewportScroller,
    private gtmService: GtmService,
    private sentryLogger: SentryLogger,
    private changeDetectorRef: ChangeDetectorRef,
    private store$: Store<CommonBookingFeatureState>
  ) {}

  ngOnInit(): void {
    this.currentFlightTail = resolveTailName(this.flight.operatingAirline.code);

    this.subscriptions.add(
      combineLatest([this.passengers$, this.seatSelections$, this.flight.seatMap$])
        .pipe(
          take(1),
          switchMap(([passengers, seatSelections, seatMap]) => {
            return this.preSelectedPax$.pipe(
              distinctUntilChanged(),
              map((preSelectedPax) => {
                return [passengers, seatSelections, seatMap, preSelectedPax] as [
                  FinnairPassengerItem[],
                  ServiceCatalogSelectionsForAllTravelers,
                  SeatmapSvgAndControlData,
                  string,
                ];
              })
            );
          })
        )
        .subscribe(([passengers, seatSelections, seatMap, preSelectedPax]) => {
          this.selectablePassengers$.next(this.mapPassengers(passengers, seatSelections, seatMap, preSelectedPax));
        })
    );

    this.passengerOptions$ = this.selectablePassengers$.pipe(
      withLatestFrom(this.languageService.translate('seatMap.and')),
      map(([passengers, and]) => passengers.map((p) => ({ value: p.id, name: this.getTravelerFullName(p, and) })))
    );

    this.bundleActive$ = this.selectablePassengers$.pipe(
      map((passengers) => {
        const activePassenger = passengers.find((passenger) => passenger.active);
        return !!activePassenger?.bundleActive;
      }),
      distinctUntilChanged()
    );

    this.bundlesEnabled = this.configService.cfg.enableBookingFlowAncillaryBundles;

    this.subscriptions.add(
      this.flight.seatMap$.subscribe((seatMap) => {
        this.isFinnairWetLease = seatMap.isWetlease;

        // Wait until seat map element is visible before triggering seat chips to render.
        setTimeout(() => {
          const seatMapEl = this.seatMapElement?.seatMapWrapper?.nativeElement;
          this.seatMapContainerWidth$.next(seatMapEl?.clientWidth);
          this.changeDetectorRef.detectChanges();
        }, 0);
      })
    );

    this.subscriptions.add(
      combineLatest([
        this.selectablePassengers$,
        this.flightIsActive$.pipe(delay(FLIGHT_CONTAINER_ANIMATION_DELAY)), // Delay to prevent transition collision, to be refactored
        this.flight.seatMapStatus$,
        this.ancillaryLoader$,
        this.cartOrOrder$,
      ])
        .pipe(
          filter(
            ([_pax, active, seatMapStatus, _loaderStatus, _order]) => active && seatMapStatus === SeatMapStatus.OK
          ),
          take(1),
          map(([pax, _active, _seatMapStatus, loaderStatus, order]) => {
            return { pax, loaderStatus, order };
          })
        )
        .subscribe(({ pax, loaderStatus, order }) => {
          if (loaderStatus === AncillaryLoaderStatus.REASSIGNING) {
            this.store$.dispatch(SeatMapActions.resetSeatSelections({ cartOrOrder: order! }));
          }
          if (pax.length > 0 && !this.skipScroll) {
            this.tryScrollToFirstAvailableSeat(pax[0]);
          }
        })
    );

    this.subscriptions.add(
      combineLatest([this.flight$, this.cartOrOrder$]).subscribe(([flight, cartOrOrder]) => {
        this.setBenefitDetails(flight, cartOrOrder);
      })
    );

    this.subscriptions.add(
      this.flight.seatMapStatus$
        .pipe(
          filter(
            (status: SeatMapStatus) =>
              status === SeatMapStatus.OK ||
              status === SeatMapStatus.LOAD_ERROR ||
              status === SeatMapStatus.OK_NO_AVAILABILITY
          )
        )
        .subscribe(() => {
          this.ancillaryLoader$.next(AncillaryLoaderStatus.OFF);
          this.isReassigningSeats.emit(false);
        })
    );

    this.unmodifiableSeat$ = this.selectablePassengers$.pipe(
      map((pax) => {
        const active = pax.find((p) => p.active);
        return active.paidSeat && !active.modifiableSeat;
      })
    );
    this.unmodifiableFreeSeat$ = this.selectablePassengers$.pipe(
      map((pax) => {
        const active = pax.find((p) => p.active);
        return !active.paidSeat && active.ticketedSeat && !active.modifiableSeat;
      })
    );

    this.operatingAirlineIdInfo$ = combineLatest([
      this.flight$.pipe(filter((flight) => !!flight)),
      this.cartOrOrder$,
    ]).pipe(
      map(([flight, cartOrOrder]) => {
        const isOrderCreated = isOrder(cartOrOrder);

        const operatingAirlineId = isOrderCreated
          ? this.getOperatingAirlineId(cartOrOrder as FinnairOrder, flight.operatingAirline.code)
          : undefined;

        return {
          operatingAirlineId,
          isOperated: isOrderCreated ? !!operatingAirlineId : flight.operatingAirline.code !== 'AY',
          isOrderIdIdentical: !!operatingAirlineId && cartOrOrder.id === operatingAirlineId.id,
          isOrder: isOrderCreated,
        };
      }),
      finShare()
    );
    this.noSeatmapNotifications$ = combineLatest([this.availability$, this.operatingAirlineIdInfo$]).pipe(
      map(([availability, operatingAirlineIdInfo]) =>
        getServicesNotifications({}, availability, Category.SEAT, this.flight, false, operatingAirlineIdInfo.isOperated)
      ),
      finShare()
    );
  }

  private getOperatingAirlineId(order: FinnairOrder, operatingAirlineCode: string): FinnairOperatingAirlineId {
    const operatingAirlineId = order.operatingAirlineIds.find(
      (operatingAirlineId) => operatingAirlineId.airlineCode === operatingAirlineCode
    );

    if (operatingAirlineId) {
      return operatingAirlineId;
    }

    const airlineName = order.bounds
      .flatMap((bound) => bound.itinerary.filter(isFlight))
      .find((flight) => flight.operatingAirline.code === operatingAirlineCode)?.operatingAirline.name;

    return airlineName && !isFinnairNorraOrWetLease(airlineName, operatingAirlineCode)
      ? {
          airlineCode: operatingAirlineCode,
          id: order.id,
          airlineName,
        }
      : undefined;
  }

  tryScrollToFirstAvailableSeat(pax: SeatMapTraveler, attempt = 0): void {
    if (attempt >= 10) {
      this.sentryLogger.error('Failed to find the seatmap after 1000ms', {
        flightNumber: this.flight.flightNumber,
        cabinClass: this.flight.cabinClass,
        id: snapshot(this.cartOrOrder$).id,
      });

      return;
    }

    setTimeout(() => {
      const seatMap = this.seatMapElement?.seatMap?.nativeElement;

      if (!seatMap) {
        const nextAttempt = attempt + 1;
        this.tryScrollToFirstAvailableSeat(pax, nextAttempt);
        return;
      }
      const seatNumber =
        pax.seat?.seatNumber ??
        seatMap?.querySelector(`[data-${pax.id.toLowerCase()}="available"]`).getAttribute('data-id');

      if (seatNumber) {
        this.scrollToSeat(seatNumber, false);
      }
    }, 100);
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  private mapPassengers(
    travelers: FinnairPassengerItem[],
    seatSelections: ServiceCatalogSelectionsForAllTravelers,
    seatMap: SeatmapSvgAndControlData,
    preSelectedPax: string | undefined
  ): SeatMapTraveler[] {
    const infants = travelers.filter((traveler) => traveler.passengerTypeCode === FinnairPassengerCode.INF);
    return travelers
      .filter((traveler) => traveler.passengerTypeCode !== FinnairPassengerCode.INF)
      .map((traveler, index) => {
        const selections = seatSelections[this.flight.id]?.find((p) => p.travelerId === traveler.id);
        const seatNumber = selections?.seatNumber;
        const originalSeatNumber = servicesForFlightAndTravelerId(this.includedSeats, this.flight.id, traveler.id)[0]
          ?.seatNumber;
        const seat = seatNumber && seatMap.seats.find((s) => s.seatNumber === seatNumber);
        const infant = infants.find((i) => i.accompanyingTravelerId === traveler.id);
        return {
          id: traveler.id,
          traveler,
          withInfant: !!infant,
          withInfantFullName: infant ? `${infant.firstName} ${infant.lastName}` : '',
          groupedServices: null,
          active: preSelectedPax ? traveler.id === preSelectedPax : index === 0,
          seat,
          seatNumber,
          originalSeatNumber,
          modifiableSeat: selections?.modifiable,
          ticketedSeat: selections?.ticketed,
          paidSeat: selections?.paid,
        };
      });
  }

  private getTravelerFullName(passenger: SeatMapTraveler, and: string): string {
    const withInfant = passenger.withInfant ? `(${and} ${passenger.withInfantFullName})` : '';
    return `${passenger.traveler.firstName} ${passenger.traveler.lastName} ${withInfant}`.trim();
  }

  selectSeat({
    passengerId,
    seatNumber,
    exitRow,
  }: {
    passengerId: string;
    seatNumber: string;
    exitRow: boolean;
  }): void {
    const isModifiableSeats: boolean =
      !this.includedSeats || isAllowedToChangeSeat(this.includedSeats, this.flight.id, passengerId);
    if (!isModifiableSeats) {
      return;
    }

    this.subscriptions.add(
      this.flight.seatMap$.pipe(take(1)).subscribe((seatMap) => {
        const shouldRevertSeat = !this.canClearSeats && seatNumber === null;
        const actualSeatNumber = shouldRevertSeat
          ? servicesForFlightAndTravelerId(this.includedSeats, this.flight.id, passengerId)[0]?.seatNumber
          : seatNumber;
        const seat: SeatInfo = seatMap.seats.find((s) => s.seatNumber === actualSeatNumber);
        if (seat && exitRow && actualSeatNumber) {
          this.seatDetails$.next({
            travelerId: passengerId,
            seat,
            seatNumber: actualSeatNumber,
            seatType: seat.type,
            price: getSeatPrice(seat, passengerId),
            exitRow,
          });
          this.exitRowModalOpen = true;
          this.gtmService.seatMapEvent('exit-row-modal', ElementTypes.MODAL, '', ElementActions.VIEW, {
            flightInfo: getSeatMapFlight(this.flight),
          });
        } else {
          this.confirmSeatSelection(passengerId, actualSeatNumber);
        }
      })
    );
  }

  confirmExitRowSeat(passengerId: string, seatNumber: string): void {
    this.confirmSeatSelection(passengerId, seatNumber);
    this.gtmService.seatMapEvent('exit-row-confirm', ElementTypes.BUTTON, 'cta', ElementActions.CLICK, {
      flightInfo: getSeatMapFlight(this.flight),
    });
  }

  confirmSeatSelection(passengerId: string, seatNumber: string): void {
    this.exitRowModalOpen = false;
    const currentPassengers = this.selectablePassengers$.getValue();
    const activePaxIndex = currentPassengers.findIndex((pax) => pax.active);
    const isLastPax = activePaxIndex === currentPassengers.length - 1;
    const isActive = (index: number): boolean => {
      if (seatNumber) {
        return isLastPax ? index === 0 : activePaxIndex + 1 === index;
      }
      return index === activePaxIndex;
    };

    this.selectablePassengers$.next(
      currentPassengers.map((pax, index) => {
        return {
          ...pax,
          ...(pax.id === passengerId && {
            seatNumber,
          }),
          active: isActive(index),
        };
      })
    );

    this.changeSeats.emit({
      travelerId: passengerId,
      seatNumber,
      flightId: this.flight.id,
    });
  }

  selectPassenger(passengerId: string): void {
    const currentPaxs = this.selectablePassengers$.getValue();

    this.selectablePassengers$.next(
      currentPaxs.map((pax) => {
        return {
          ...pax,
          active: pax.id === passengerId,
        };
      })
    );
  }

  onBundleToggle(toggled: boolean): void {
    this.selectablePassengers$.next(
      this.selectablePassengers$.getValue().map((pax) => (pax.active ? { ...pax, bundleActive: toggled } : pax))
    );
  }

  scrollSeatMap(direction: string): void {
    const seatMap = this.seatMapElement.seatMapWrapper.nativeElement;
    const divWidth = seatMap.clientWidth / 2;

    if (direction === 'left') {
      this.scrollPosition = seatMap.scrollLeft + divWidth;
      seatMap.scrollBy({
        left: divWidth,
        behavior: 'smooth',
      });
    } else {
      this.scrollPosition = seatMap.scrollLeft - divWidth;
      seatMap.scrollBy({
        left: -divWidth,
        behavior: 'smooth',
      });
    }

    this.gtmService.seatMapEvent('seatmap-full', ElementTypes.BUTTON, 'arrow', ElementActions.CLICK, {
      flightInfo: getSeatMapFlight(this.flight),
    });
  }

  scrollTo(position: number): void {
    requestAnimationFrame(() => {
      const seatMap: HTMLDivElement = this.seatMapElement.seatMapWrapper.nativeElement;

      seatMap.scrollTo({
        left: position,
        behavior: 'smooth',
      });
    });
  }

  scrollToSeat(seatNumber: string, focus = true): void {
    requestAnimationFrame(() => {
      const seatMap: HTMLDivElement = this.seatMapElement.seatMapWrapper.nativeElement;
      const seatElement: HTMLAnchorElement = seatMap.querySelector(`[data-id="${seatNumber}"] a`);

      if (!seatElement) {
        return;
      }

      /**
       * Calculate if the seat can be centered or not. If this is not done this can cause bugs
       * where the slider partially moves onto the next seatmap as the browser tries to force the
       * seat to the center of the view.
       */
      let isCenterable = false;

      try {
        let seatGElement = seatElement.parentElement as unknown as SVGGElement;

        /**
         * Business seats are wrapped in 2 levels of parent SVGGElements. So we need to go to the
         * parents parent to get the actual positioning. (The `transform: translate(100, 200)` that
         * you see) in the elements panel on dev tools.
         */
        if (!seatGElement.transform.baseVal.length) {
          seatGElement = seatGElement.parentElement as unknown as SVGGElement;
        }

        const halfViewArea = seatMap.getBoundingClientRect().width * 0.5;
        const svgWidth = seatMap.querySelector('svg').getBoundingClientRect().width;
        const seatXCoordinate = seatGElement.transform.baseVal.consolidate().matrix.e;

        isCenterable = svgWidth - seatXCoordinate > halfViewArea && seatXCoordinate > halfViewArea;
      } catch (e) {
        const cartOrOrderId = snapshot(this.cartOrOrder$).id;
        this.sentryLogger.error('Failed to calculate seatmap scroll to seat', {
          message: e.message,
          stack: e.stack.substr(0, 500),
          flightNumber: this.flight.flightNumber,
          cabinClass: this.flight.cabinClass,
          // A quick filter for PNR's, so that only cart ids are reported to Sentry.
          id: cartOrOrderId.length === 6 ? null : cartOrOrderId,
        });
      }

      seatElement.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: isCenterable ? 'center' : 'nearest',
      });

      if (focus) {
        seatElement.focus({ preventScroll: true });
      }
    });
  }

  moveFocus(e: Event, el: string): void {
    stopPropagation(e);
    this.viewportScroller.scrollToAnchor(el);
  }

  enableScrollButtons(event: 'left' | 'center' | 'right'): void {
    this.isAtStart = event === 'left';
    this.isAtEnd = event === 'right';
  }

  openAirplaneModal(): void {
    this.aircraftModalOpen = true;
    const fragmentKey = `fragments.airCraftInfo.${this.getAircraftTranslationKey()}.url`;
    this.subscriptions.add(
      this.languageService
        .translate(fragmentKey)
        .pipe(
          filter(Boolean),
          switchMap((url) => this.templateService.load(url))
        )
        .subscribe((template) => this.aircraftInformationTemplate$.next(template))
    );
    this.gtmService.seatMapEvent('aircraft-info-modal', ElementTypes.MODAL, '', ElementActions.VIEW, {
      flightInfo: getSeatMapFlight(this.flight),
    });
  }

  setBenefitDetails(flight: FinnairItineraryItemFlight, order: FinnairOrder | FinnairCart): void {
    const bound = order.bounds.find((bound) =>
      bound.itinerary.some((itinerary: FinnairItineraryItemFlight) => itinerary.id === flight.id)
    );
    const hasFareFamilySeatBenefit = order.benefits?.[bound.fareFamily.code]?.translatedBenefitRows.find(
      (benefit) => benefit.key === BENEFITS.seatSelection
    )?.isPositive;
    const hasCorporateSeatBenefit =
      !hasFareFamilySeatBenefit && order.otherInformation?.corporateUserInformation?.isEligibleForSeatBenefit;
    if (hasCorporateSeatBenefit) {
      this.benefitTextKey = 'seatMap.benefits.corporateBenefitDescription';
      this.benefitTag = {
        textKey: 'seatMap.benefits.corporateBenefitTag',
        theme: TagTheme.DEFAULT,
        icon: {
          name: SvgLibraryIcon.B2B,
        },
      };
    }
  }

  private getAircraftTranslationKey(): string | undefined {
    const code = this.flight.aircraft.code;
    if (!AIRCRAFT_TRANSLATION_MAP[code]) {
      this.sentryLogger.error('Invalid aircraft model', { aircraft: this.flight.aircraft });
    }

    return AIRCRAFT_TRANSLATION_MAP[code];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.flight) {
      this.flight$.next(changes.flight.currentValue);
    }
  }
}
